<template>
  <b-modal
    :visible="visible"
    title="Nouvel article"
    size="lg"
    centered
    no-fade
    ok-title="Créer la proposition"
    :ok-disabled="!canSubmit || submitting"
    cancel-title="Annuler"
    cancel-variant="outline-secondary"
    @change="$emit('update:visible', $event)"
    @ok.prevent="submit"
    @hide="onHide"
  >
    <b-form @submit.prevent="submit">
      <b-form-group label="Titre" label-for="new-article-title" :state="titleState">
        <b-form-input
          id="new-article-title"
          v-model="title"
          :state="titleState"
          maxlength="200"
          placeholder="Titre de l'article tel qu'affiché côté Chatwoot"
          autofocus
        />
        <b-form-invalid-feedback :state="titleState">
          Le titre est obligatoire (≤ 200 caractères).
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Slug" label-for="new-article-slug" :state="slugState">
        <b-input-group>
          <b-form-input
            id="new-article-slug"
            v-model="slug"
            :state="slugState"
            maxlength="100"
            placeholder="ex. comment-creer-un-contrat"
            @input="onSlugManualEdit"
          />
          <b-input-group-append>
            <b-button variant="outline-secondary" :disabled="!title" @click="resyncSlugFromTitle">
              <feather-icon icon="RefreshCwIcon" /> Du titre
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <b-form-text v-if="slug && checkingSlug" class="text-muted">
          <b-spinner small /> Vérification…
        </b-form-text>
        <b-form-text v-else-if="slug && slugCheckResult && slugCheckResult.available" class="text-success">
          ✓ Slug libre
        </b-form-text>
        <b-form-text v-else-if="slug && slugCheckResult && !slugCheckResult.available" class="text-danger">
          ✗ Un {{ slugCheckResult.conflict === 'published' ? 'article publié' : 'brouillon' }}
          existe déjà avec ce slug.
        </b-form-text>
        <b-form-invalid-feedback :state="slugState">
          Le slug doit matcher <code>^[a-z0-9-]+$</code> et être libre.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Catégorie" label-for="new-article-category" :state="categoryState">
        <b-form-select
          id="new-article-category"
          v-model="category"
          :state="categoryState"
          :options="categoryOptions"
        />
        <b-form-invalid-feedback :state="categoryState">
          Catégorie obligatoire.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group
        label="Repos liés (related_repos)"
        :description="relatedReposHelp"
      >
        <div class="related-repos-input">
          <b-badge
            v-for="(tag, idx) in relatedRepos"
            :key="tag + '-' + idx"
            variant="light-primary"
            class="mr-1 mb-1 related-repo-tag"
          >
            <code>{{ tag }}</code>
            <span class="ml-1 related-repo-remove" @click="removeTag(idx)">×</span>
          </b-badge>
          <b-form-input
            v-model="newTag"
            class="related-repo-input"
            placeholder="ex. ubicx/buxapi:api/ (Entrée pour ajouter)"
            @keyup.enter="commitNewTag"
            @blur="commitNewTag"
          />
        </div>
        <small v-if="tagError" class="text-danger d-block mt-1">{{ tagError }}</small>
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script>
// Modale de creation d'un nouvel article. Phase 3 du plan.
//
// Flow :
// 1. Admin saisit titre -> slug auto-genere depuis le titre (slugify).
//    Si l'admin edite le slug a la main, on stoppe l'auto-sync.
// 2. A chaque saisie de slug, debounce 400ms, appel
//    GET /admin/docs/check-slug/:slug pour verifier la disponibilite
//    dans articles/ et articles/_proposals/.
// 3. A la validation, on assemble un MD minimal :
//      ---
//      title: <titre>
//      slug: <slug>
//      chatwoot_category: BUXIDA
//      chatwoot_status: draft
//      chatwoot_locale: fr_FR
//      category: <category>
//      related_repos:
//        - <repo1>
//      proposal_source: admin-ui@<C_UI admin>
//      proposal_created_at: <ISO>
//      proposal_summary: "Nouvel article cree depuis l'admin"
//      ---
//      <!-- Body vide, a remplir dans l'editeur -->
//    Puis POST /admin/docs/proposals { slug, content }.
// 4. On emet @created(slug) -> parent redirige sur DocsDetail.vue.

import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormSelect,
  BFormText,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import axios from '@axios'

const DOCS_API_BASE = 'https://api2.buxida.com'

const FALLBACK_CATEGORIES = [
  'demarrer',
  'reservation-en-ligne',
  'au-quotidien',
  'communication-client',
  'comptabilite-stats',
  'integrations',
  'aide',
]
const RELATED_REPO_RE = /^[a-z0-9._-]+\/[a-z0-9._-]+:.+$/i
const SLUG_RE = /^[a-z0-9-]+$/

function slugifyTitle(s) {
  if (!s) return ''
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

export default {
  name: 'NewArticleModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BFormText,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BBadge,
    BSpinner,
  },
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      title: '',
      slug: '',
      slugManuallyEdited: false,
      category: '',
      relatedRepos: [],
      newTag: '',
      tagError: '',
      categories: FALLBACK_CATEGORIES.slice(),
      checkingSlug: false,
      slugCheckResult: null,
      slugDebounceTimer: null,
      submitting: false,
      relatedReposHelp:
        'Format : owner/repo:prefix — ex. ubicx/buxapi:api/r_resa.js. Le skill /update-buxida-docs s\'en sert pour matcher les fichiers modifiés.',
    }
  },
  computed: {
    titleState() {
      const t = (this.title || '').trim()
      if (!this.touched) return null
      if (!t) return false
      if (t.length > 200) return false
      return null
    },
    slugState() {
      if (!this.touched) return null
      if (!this.slug) return false
      if (!SLUG_RE.test(this.slug)) return false
      if (this.slugCheckResult && !this.slugCheckResult.available) return false
      return null
    },
    categoryState() {
      if (!this.touched) return null
      return this.category ? null : false
    },
    categoryOptions() {
      return [
        { value: '', text: '— Choisir une catégorie —', disabled: true },
        ...this.categories.map(s => ({ value: s, text: this.formatCategoryLabel(s) })),
      ]
    },
    canSubmit() {
      return !!(this.title || '').trim()
        && SLUG_RE.test(this.slug || '')
        && this.slugCheckResult && this.slugCheckResult.available
        && !!this.category
    },
    touched() {
      // On considere le formulaire "touche" des qu'un champ est modifie.
      // Sert a ne pas afficher les invalid feedbacks tant que l'admin
      // n'a pas commence a saisir.
      return !!(this.title || this.slug || this.category || this.relatedRepos.length)
    },
  },
  watch: {
    visible(val) {
      if (val) this.loadCategories()
    },
    title(val) {
      if (!this.slugManuallyEdited) {
        this.slug = slugifyTitle(val)
        this.queueSlugCheck()
      }
    },
    slug() {
      this.queueSlugCheck()
    },
  },
  methods: {
    async loadCategories() {
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/categories`)
        const data = response.data || {}
        if (data.res === 1 && Array.isArray(data.items) && data.items.length) {
          this.categories = data.items
        }
      } catch (_) { /* fallback */ }
    },
    onSlugManualEdit() {
      this.slugManuallyEdited = true
    },
    resyncSlugFromTitle() {
      this.slug = slugifyTitle(this.title)
      this.slugManuallyEdited = false
    },
    queueSlugCheck() {
      if (this.slugDebounceTimer) clearTimeout(this.slugDebounceTimer)
      this.slugCheckResult = null
      const s = this.slug
      if (!s || !SLUG_RE.test(s)) return
      this.slugDebounceTimer = setTimeout(() => this.doSlugCheck(s), 400)
    },
    async doSlugCheck(slug) {
      if (slug !== this.slug) return // changed since
      this.checkingSlug = true
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/check-slug/${encodeURIComponent(slug)}`)
        const data = response.data || {}
        if (slug !== this.slug) return // changed during await
        if (data.res === 1) this.slugCheckResult = data.result
      } catch (_) {
        // Tolerant : si l'API repond mal on laisse l'admin tenter et le
        // backend refusera proprement en cas de collision (BUXIDA_DOCS_PROPOSAL_EXISTS).
        this.slugCheckResult = { slug, available: true, conflict: null }
      } finally {
        this.checkingSlug = false
      }
    },
    commitNewTag() {
      const v = (this.newTag || '').trim()
      if (!v) return
      if (!RELATED_REPO_RE.test(v)) {
        this.tagError = 'Format attendu : owner/repo:prefix (ex. ubicx/buxapi:api/).'
        return
      }
      if (this.relatedRepos.includes(v)) {
        this.tagError = 'Ce repo est déjà dans la liste.'
        return
      }
      this.relatedRepos.push(v)
      this.newTag = ''
      this.tagError = ''
    },
    removeTag(idx) {
      this.relatedRepos.splice(idx, 1)
    },
    formatCategoryLabel(slug) {
      if (!slug) return slug
      return slug
        .split('-')
        .map(w => (w.length ? w[0].toUpperCase() + w.slice(1) : ''))
        .join(' ')
    },
    buildContent() {
      const now = new Date().toISOString()
      const lines = [
        '---',
        `title: ${this.title.trim().replace(/"/g, '\\"')}`,
        `slug: ${this.slug}`,
        'chatwoot_category: BUXIDA',
        'chatwoot_status: draft',
        'chatwoot_locale: fr_FR',
        `category: ${this.category}`,
      ]
      if (this.relatedRepos.length) {
        lines.push('related_repos:')
        this.relatedRepos.forEach(r => lines.push(`  - ${r}`))
      } else {
        lines.push('related_repos: []')
      }
      lines.push('proposal_source: admin-ui')
      lines.push(`proposal_created_at: '${now}'`)
      lines.push('proposal_summary: "Nouvel article créé depuis l\'admin"')
      lines.push('---')
      lines.push('')
      lines.push('<!-- Body vide, à remplir dans l\'éditeur -->')
      lines.push('')
      return lines.join('\n')
    },
    async submit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      try {
        const content = this.buildContent()
        const response = await axios.post(
          `${DOCS_API_BASE}/admin/docs/proposals`,
          { slug: this.slug, content },
        )
        const data = response.data || {}
        if (data.res === 1) {
          this.$emit('created', { slug: this.slug })
          this.$emit('update:visible', false)
        } else {
          this.$emit('error', (data && data.detail) || 'Création échouée')
        }
      } catch (err) {
        this.$emit('error', this.formatErr(err))
      } finally {
        this.submitting = false
      }
    },
    onHide() {
      // Reset complet a la fermeture pour que la prochaine ouverture
      // ne traine pas l'etat du formulaire precedent.
      this.title = ''
      this.slug = ''
      this.slugManuallyEdited = false
      this.category = ''
      this.relatedRepos = []
      this.newTag = ''
      this.tagError = ''
      this.checkingSlug = false
      this.slugCheckResult = null
      if (this.slugDebounceTimer) { clearTimeout(this.slugDebounceTimer); this.slugDebounceTimer = null }
      this.submitting = false
    },
    formatErr(err) {
      return (err && err.response && err.response.data && err.response.data.detail)
        || (err && err.message)
        || 'Erreur inconnue'
    },
  },
}
</script>

<style scoped>
.related-repos-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.related-repo-tag {
  font-size: 0.85rem;
  padding: 4px 8px;
}
.related-repo-tag code {
  background: transparent;
  color: inherit;
  font-size: 0.95em;
}
.related-repo-remove {
  cursor: pointer;
  font-weight: bold;
  opacity: 0.6;
}
.related-repo-remove:hover {
  opacity: 1;
}
.related-repo-input {
  min-width: 240px;
  flex: 1;
}
</style>
